import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { useMediaQuery, useTheme } from '@mui/material'
import { GridRow, useGridApiContext } from '@mui/x-data-grid'

import {
  formatContributionDescription,
  formatDafTransferDescription,
  toAmountString,
  toDateStringInTimezone,
  toDateStringNumeric,
} from '../../utils'
import { useTimezone } from '../../hooks'
import { mutations, queries } from '../../graphql'
import { Box, Button, CancelButton, CardBox, Container, DataGrid, Icons, Link, Stack, Text } from '../../components'
import { ActionButton } from '../../management/management-components/actions/ActionButton'
import { RequestInvestmentTransferDialog } from './initiate-transfer-dialog'
import { Filters } from './filters'
import {
  investmentTransferDescription,
  investmentTransferDirection,
  investmentTransferRowDate,
  investmentTransferStateLabel,
  wrappedLineCount,
} from './investment-activity-helpers'

// FLIGHT-296 follow-up: same "only Description wraps, everything else stays
// single-line" treatment as investment-portfolio-activity.jsx, adapted to
// this grid's own numbers -- it uses autoRowHeight's true MUI X 'auto'
// measurement today (not the fixed-rowHeight-plus-getRowHeight workaround
// that page uses), and much tighter cell padding (3px here vs the default
// 10px there), so a straight copy of that page's pixel constants would
// roughly double every row's height. BASE_ROW_HEIGHT instead mirrors what
// 'auto' already renders for a single-line row here: Icons.State's pill is
// the tallest single-line cell content on this page (a fixed 26px, taller
// than a plain 20px text line), plus this grid's own 3px top+bottom cell
// padding.
const BASE_ROW_HEIGHT = 32
const DESCRIPTION_LINE_HEIGHT = 20
// This grid's own cell padding (`p: '3px'` in the DataGrid's sx below) --
// subtracted from the Description column's computed width to get the
// actual width its text has to wrap within.
const DESCRIPTION_CELL_HORIZONTAL_PADDING = 6
// Same reasoning as investment-portfolio-activity.jsx's CELL_TOP_PADDING:
// top-align every cell at the offset centering would've put a single line
// at, so a wrapped Description row only grows downward instead of
// centering its whole multi-line block above/below the rest of that row.
const CELL_TOP_PADDING = (BASE_ROW_HEIGHT - DESCRIPTION_LINE_HEIGHT) / 2

function useIsNarrowerThan(sz) {
  const theme = useTheme()
  return !useMediaQuery(theme.breakpoints.up(sz))
}

/* Shown as column on rows to toggle expand / collapse. */
function RowExpandButton({ id, isExpanded, toggleExpanded, sx, ...props }) {
  return (
    <Button sx={{ flexGrow: 1, p: 0, minWidth: 0, ...sx }} unstyled {...props} onClick={() => toggleExpanded(id)}>
      {isExpanded(id) ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
    </Button>
  )
}

function useColumnsForExpandPanel() {
  const apiRef = useGridApiContext()
  /* showInExpandedRowPanel can exclude some columns from rendering in the expanded panel.
   * Otherwise, show columns that have been hidden. */
  return apiRef.current
    .getAllColumns()
    .filter(({ showInExpandedRowPanel = true, hide = false }) => showInExpandedRowPanel && hide)
}

/* A GridRow component with that conditionally shows an ExpandedRowPanel. */
function ExpandableRow({ isExpanded, rowId, ...props }) {
  const isNarrowerThanMd = useIsNarrowerThan('md')
  return (
    <div>
      <GridRow rowId={rowId} {...props} />
      {isExpanded(rowId) && isNarrowerThanMd && <ExpandedRowPanel data-expanded={rowId} rowId={rowId} />}
    </div>
  )
}

function ExpandedRowPanel({ rowId, sx, ...props }) {
  const apiRef = useGridApiContext()
  const fields = useColumnsForExpandPanel()
    .map(({ field, headerName, renderCell }) => {
      const cellParams = apiRef.current.getCellParams(rowId, field)
      const content =
        renderCell?.({ ...cellParams, api: apiRef.current }) ??
        cellParams.formattedValue?.toString() ??
        cellParams.value?.toString()
      return { key: field, headerName, content }
    })
    .filter(({ content }) => content)

  const justify = useIsNarrowerThan('sm') ? 'space-between' : 'start'

  return (
    <Stack spacing={0} sx={{ pl: 7, ...sx }} {...props}>
      {fields.map(({ key, headerName, content }) => (
        <Stack key={key} direction="row" justifyContent={justify} minHeight="28px" alignItems="center">
          <div style={{ flex: 1, maxWidth: '140px' }}>{headerName}</div>
          <div>{content}</div>
        </Stack>
      ))}
    </Stack>
  )
}

export function GivingWallet() {
  const timezone = useTimezone()
  const isNarrowerThanMd = useIsNarrowerThan('md')
  const isNarrowerThanSm = useIsNarrowerThan('sm')

  const gridRef = useRef()
  const gridApi = useRef()

  // Per-row height: BASE_ROW_HEIGHT unless the Description column's own text
  // needs to wrap onto more than one line at its actual rendered width --
  // see investment-portfolio-activity.jsx's identical getRowHeight for the
  // full reasoning (a plain number here, never 'auto', to avoid MUI X's own
  // buggy auto-measurement path). Description is hidden below the `sm`
  // breakpoint (see the description column def), in which case
  // getColumn('description') has no computedWidth and this falls back to
  // BASE_ROW_HEIGHT -- correct, since there's no visible Description cell to
  // wrap there. The expanded-row-height pipe processor below adds on top of
  // whatever this returns, same as it already does today.
  const getRowHeight = useCallback(({ model }) => {
    const descriptionWidth = gridApi.current?.getColumn?.('description')?.computedWidth
    if (!descriptionWidth) return BASE_ROW_HEIGHT

    // model.description is a [charity, description, processingNote] tuple on
    // GrantRequest rows (see that row mapping above), not a string --
    // GrantRequestDescriptionRender only ever displays charity.accountName
    // for those rows, so that's what this measures against too.
    const descriptionText = model.isGrantRequest ? model.description?.[0]?.accountName : model.description
    const lines = wrappedLineCount(descriptionText, descriptionWidth - DESCRIPTION_CELL_HORIZONTAL_PADDING)
    return BASE_ROW_HEIGHT + (lines - 1) * DESCRIPTION_LINE_HEIGHT
  }, [])

  // Same reasoning as investment-portfolio-activity.jsx's own resize
  // observer -- the Description column's width (and so its wrap point)
  // changes on window resize, but a plain-number getRowHeight doesn't get
  // MUI X's own remeasure-on-resize handling the way 'auto' would. Observes
  // the grid's own root DOM node directly (gridRef) rather than adding
  // another wrapping element.
  useEffect(() => {
    const element = gridRef.current
    if (!element) return undefined
    const observer = new ResizeObserver(() => gridApi.current?.resetRowHeights())
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const [showContributions, setShowContributions] = useState(true)
  const [showGrantRequests, setShowGrantRequests] = useState(true)
  const [showBalanceAdjustements, setShowBalanceAdjustements] = useState(true)
  const [showDafTransfers, setShowDafTransfers] = useState(true)
  const [showInvestmentTransfers, setShowInvestmentTransfers] = useState(true)
  const [requestTransferOpen, setRequestTransferOpen] = useState(false)

  const fundId = Number(useParams().fundId)
  const {
    loading,
    data: {
      me: {
        fund: {
          contributions = [],
          grantRequests = [],
          balanceAdjustments = [],
          dafTransfers = [],
          investmentTransfers = [],
          investmentPortfolios = [],
        } = {},
      } = {},
    } = {},
  } = useQuery(queries.funds.myFundActivity, {
    variables: { fundId },
  })

  const { data: { me: { fund } = {} } = {} } = useQuery(queries.funds.myFund, {
    variables: { fundId },
  })

  const gWContributions = useMemo(() => contributions.filter((c) => c.walletType === 'giving'), [contributions])

  const gWBalanceAdjustments = useMemo(
    // FLIGHT-295 follow-up: excludes rows already covered by their own
    // Investment Transfer row below (investmentTransferId set) -- otherwise
    // the same money movement would show twice once a transfer actually
    // posts.
    () => balanceAdjustments.filter((ba) => ba.walletType === 'giving' && !ba.investmentTransferId),
    [balanceAdjustments]
  )
  const gWInvestmentTransfers = useMemo(
    () =>
      investmentTransfers
        .map((t) => ({ transfer: t, direction: investmentTransferDirection(t, { fundId }) }))
        .filter(({ direction }) => direction),
    [investmentTransfers, fundId]
  )
  const initiatedDafTransfers = useMemo(
    () => dafTransfers.filter(({ initiatingFundId }) => initiatingFundId === fundId),
    [dafTransfers, fundId]
  )
  const receivedDafTransfers = useMemo(
    () => dafTransfers.filter(({ receivingFundId }) => receivingFundId === fundId),
    [dafTransfers, fundId]
  )

  const standardReceivedDafTransferMap = (dafTransfer) => ({
    ...dafTransfer,
    id: `rdt${dafTransfer.id}`,
    recordId: dafTransfer.id,
    date: dafTransfer?.canceledAt ?? dafTransfer?.receivedAt ?? dafTransfer?.createdAt,
    dateTable: dafTransfer?.canceledAt ?? dafTransfer?.receivedAt ?? dafTransfer?.createdAt,
    description: formatDafTransferDescription(dafTransfer, { type: 'recevived' }),
  })

  const generosityFundDafTransferMap = (dafTransfer) => ({
    ...standardReceivedDafTransferMap(dafTransfer),
    ...{ email: dafTransfer.initiatingUserEmail },
  })

  useEffect(() => {})
  const rows = [
    ...(showDafTransfers
      ? [
          ...initiatedDafTransfers.map((dafTransfer) => ({
            ...dafTransfer,
            id: `idt${dafTransfer.id}`,
            recordId: dafTransfer.id,
            showAsDebit: true,
            date: dafTransfer?.canceledAt ?? dafTransfer?.receivedAt ?? dafTransfer?.createdAt,
            dateTable: dafTransfer?.canceledAt ?? dafTransfer?.receivedAt ?? dafTransfer?.createdAt,
            description: formatDafTransferDescription(dafTransfer, { type: 'initiated' }),
            grossAmount: dafTransfer.amount,
          })),
          ...receivedDafTransfers.map((dafTransfer) =>
            fund?.isGenerosityFund
              ? generosityFundDafTransferMap(dafTransfer)
              : standardReceivedDafTransferMap(dafTransfer)
          ),
        ]
      : []),
    ...(showBalanceAdjustements
      ? gWBalanceAdjustments.map((balanceAdjustment) => ({
          ...balanceAdjustment,
          id: `ba${balanceAdjustment.id}`,
          recordId: balanceAdjustment.id,
          state: 'posted',
          date: balanceAdjustment?.createdAt,
          dateTable: balanceAdjustment?.createdAt,
          grossAmount: balanceAdjustment.amount,
          netAmount: balanceAdjustment.amount,
        }))
      : []),
    // FLIGHT-295 follow-up: shows from the moment a transfer is Requested,
    // before any BalanceAdjustment exists -- the ledger row above is
    // suppressed (via gWBalanceAdjustments' investmentTransferId filter)
    // once this row's own transfer actually posts one, so the same money
    // movement is never shown twice. amount (signed) drives the credit/
    // debit icon color in Icons.ActivityItem, same convention as the
    // BalanceAdjustment case there; grossAmount/showAsDebit drive this
    // page's own Amount/Gross Amount columns, this file's own convention
    // (distinct from investment-portfolio-activity.jsx's signed-amount-only
    // convention -- each new row matches its own file's existing shape
    // rather than forcing one style onto both).
    ...(showInvestmentTransfers
      ? gWInvestmentTransfers.map(({ transfer: t, direction }) => ({
          ...t,
          id: `it${t.id}`,
          recordId: t.id,
          __typename: 'InvestmentTransfer',
          showAsDebit: direction === 'outgoing',
          amount: direction === 'outgoing' ? -t.amount : t.amount,
          date: investmentTransferRowDate(t),
          dateTable: investmentTransferRowDate(t),
          description: investmentTransferDescription(t, direction),
          grossAmount: t.amount,
          state: investmentTransferStateLabel(t),
        }))
      : []),
    ...(showGrantRequests
      ? grantRequests.map((grantRequest) => ({
          ...grantRequest,
          id: `gr${grantRequest.id}`,
          recordId: grantRequest.id,
          date: grantRequest?.processingDate ? grantRequest.processingDate : grantRequest?.createdAt,
          dateTable: grantRequest?.processingDate ? grantRequest.processingDate : grantRequest?.createdAt,
          showAsDebit: true,
          isGrantRequest: true,
          description: [grantRequest?.charity, grantRequest?.description, grantRequest?.processingNote],
          grossAmount: grantRequest.amount,
        }))
      : []),
    ...(showContributions
      ? gWContributions.map((contribution) => ({
          ...contribution,
          id: `c${contribution.id}`,
          recordId: contribution.id,
          date: contribution?.receiptedDate,
          dateTable: contribution?.receiptedDate,
          description: formatContributionDescription(contribution),
          grossAmount: contribution.amount,
        }))
      : []),
  ]

  const [expandMap, setExpandMap] = useState({})
  const isExpanded = useCallback((rowId) => expandMap[rowId] || false, [expandMap])
  const toggleExpanded = useCallback((rowId) => setExpandMap({ ...expandMap, [rowId]: !expandMap[rowId] }), [expandMap])
  const expandedHeight = useCallback(
    (rowId) => (expandMap[rowId] && gridRef?.current?.querySelector(`[data-expanded=${rowId}]`)?.clientHeight) || 0,
    [expandMap, gridRef]
  )

  function getTypeName(value, usedGiveGenius) {
    switch (value) {
      case 'GrantRequest':
        return usedGiveGenius ? 'GiveGenius' : 'Gifts'
      case 'DafTransfer':
        return 'Shared Funds'
      case 'InvestmentTransfer':
        return 'Investment Transfer'
      default:
        return value
    }
  }

  /* hide means is hidden from the columnar display and shown in ExpandedRowPanel.
   * showInExpandedRowPanel is a boolean that, when false, excludes showing that column in the expanded panel.
   *
   * Some columns have both because they are never shown in the datagrid but should appear in the csv export... */
  const columns = [
    {
      /* field is required so we have to provide a value, but no value makes
       * sense because this does not map to a property on the row */
      field: '__expand',
      disableExport: true,
      hide: !isNarrowerThanMd,
      sortable: false,
      filterable: false,
      align: 'center',
      minWidth: 44,
      maxWidth: 44,
      renderCell: ({ row } = {}) => (
        <RowExpandButton id={row.id} isExpanded={isExpanded} toggleExpanded={toggleExpanded} />
      ),
      renderHeader: () => null,
      showInExpandedRowPanel: false,
    },
    {
      field: '__typename',
      headerName: 'Transaction',
      flex: 0.5,
      align: 'center',
      minWidth: 44,
      maxWidth: 44,
      valueFormatter: ({ value }) => getTypeName(value),
      renderCell: ({ row } = {}) => <Icons.ActivityItem row={row} />,
      renderHeader: () => null,
      showInExpandedRowPanel: false,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 100,
      type: 'date',
      hide: true,
      align: isNarrowerThanSm && 'right',
      headerAlign: isNarrowerThanSm && 'right',
      valueFormatter: ({ value }) => toDateStringNumeric(value),
    },
    {
      field: 'dateTable',
      headerName: 'Date',
      flex: 1,
      // "Sep 22, 2026" (toDateStringInTimezone, month: 'short' -- every
      // English month abbreviation is 3 letters, so this is already the
      // worst case) only needs ~90px -- some of this column's old slack was
      // reclaimed for State below.
      minWidth: 95,
      type: 'date',
      disableExport: true,
      align: isNarrowerThanSm && 'right',
      headerAlign: isNarrowerThanSm && 'right',
      valueFormatter: ({ value }) => toDateStringInTimezone(value, timezone),
    },
    {
      field: 'description',
      headerName: 'Description',
      hide: isNarrowerThanSm,
      flex: 1,
      // Reclaimed for State below, and shrunk further than that reallocation
      // alone needed -- this grid's own visible-column minWidths (44+95+420
      // +80+175+40=854) plus its Container/CardBox padding (~112px) added up
      // to more room than is actually available right at the `md` breakpoint
      // (900px, where State/Cancel/the mobile expand button toggle), so the
      // grid scrolled instead of laying out cleanly the moment a viewport
      // crossed into "desktop" width. 340 (total 774) clears that with some
      // margin -- safe to take from here specifically since this is the one
      // column that wraps instead of clipping if it ever runs short on room
      // (see renderCell/getRowHeight).
      minWidth: 340,
      sortable: false,
      // The only column allowed to wrap -- see getRowHeight above. lineHeight
      // is explicit, not the font's own default, so the browser's real
      // wrapped line spacing matches what getRowHeight assumed when it sized
      // the row (see DESCRIPTION_LINE_HEIGHT).
      renderCell: ({ api: { getRow } = {}, id, value }) => (
        <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: `${DESCRIPTION_LINE_HEIGHT}px` }}>
          <GrantRequestDescriptionRender
            descriptionValue={value}
            fundId={fundId}
            isGrantRequest={getRow(id)?.isGrantRequest}
          />
        </Box>
      ),
      valueFormatter: ({ api: { getRow } = {}, id, value }) => {
        if (getRow(id)?.isGrantRequest) {
          const [charity] = value
          return `${charity?.accountName} (${charity?.id})` || ''
        }
        return value
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      hide: true,
      showInExpandedRowPanel: false,
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableExport: !fund?.isGenerosityFund,
    },
    {
      field: 'grantNote',
      headerName: 'Gift Designation',
      flex: 2,
      hide: true,
      minWidth: 150,
      valueFormatter: ({ api: { getRow } = {}, id, value }) => {
        if (getRow(id)?.isGrantRequest) {
          const [, description, processingNote] = getRow(id)?.description || []
          return [description, processingNote].filter(Boolean).join(', ')
        }
        return value
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      minWidth: 80,
      disableExport: true,
      // FLIGHT-295 follow-up: unlike grossAmount/tip/fee/netAmount below,
      // this column never shows a leading "-" for any row type -- Cristie's
      // request that direction be conveyed only via the icon/description,
      // not the number itself. The underlying signed `amount` field is left
      // untouched (it still drives Icons.ActivityItem's credit/debit icon
      // color for BalanceAdjustment/InvestmentTransfer rows); only this
      // column's own display is forced to the absolute value.
      valueFormatter: ({ value } = {}) => toAmountString(Math.abs(value)),
      renderCell: ({ value } = {}) => <Text.Body>{toAmountString(Math.abs(value))}</Text.Body>,
    },
    {
      field: 'grossAmount',
      headerName: 'Gross Amount',
      flex: 1,
      minWidth: 80,
      hide: true,
      valueFormatter: ({ api: { getRow } = {}, id, value }) => {
        const row = getRow(id)
        return row?.showAsDebit ? toAmountString(-Math.abs(value)) : toAmountString(value)
      },
      renderCell: ({ value, row: { showAsDebit } = {} } = {}) => (
        <Text.Body>{toAmountString(value, showAsDebit)}</Text.Body>
      ),
    },
    {
      field: 'tip',
      headerName: 'Courtesy Fee',
      description: 'Amount you gave to cover GiveWise’s transaction fees',
      hide: true,
      showInExpandedRowPanel: false,
      flex: 1,
      minWidth: 70,
      valueFormatter: ({ api: { getRow } = {}, id, value }) => {
        const row = getRow(id)
        return row?.showAsDebit ? toAmountString(-Math.abs(value)) : toAmountString(value)
      },
    },
    {
      field: 'fee',
      headerName: 'Processing Fee',
      hide: true,
      showInExpandedRowPanel: false,
      flex: 1,
      minWidth: 70,
      valueFormatter: ({ api: { getRow } = {}, id, value }) => {
        const row = getRow(id)
        return row?.showAsDebit ? toAmountString(-Math.abs(value)) : toAmountString(value)
      },
    },
    {
      field: 'netAmount',
      headerName: 'Net Amount',
      hide: true,
      showInExpandedRowPanel: false,
      flex: 1,
      minWidth: 80,
      valueFormatter: ({ api: { getRow } = {}, id, value }) => {
        const row = getRow(id)
        return row?.showAsDebit ? toAmountString(-Math.abs(value)) : toAmountString(value)
      },
    },
    {
      field: 'isRecurring',
      headerName: 'Recurring',
      hide: true,
      showInExpandedRowPanel: false,
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableExport: true,
    },
    {
      field: 'usedGiveGenius',
      headerName: 'GiveGenius',
      hide: true,
      showInExpandedRowPanel: false,
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableExport: true,
    },
    {
      field: 'state',
      headerName: 'State',
      hide: isNarrowerThanMd,
      flex: 1,
      // Same reasoning as investment-portfolio-activity.jsx's State column --
      // Icons.State's renderCell bypasses MUI's own ellipsis handling, so an
      // overflow here is a silent hard clip, not a "...". The longest
      // possible label ("Approved: Pending", from investmentTransferStateLabel)
      // needs ~164px against this grid's own (tighter, 3px) cell padding;
      // 175 covers that with a small safety margin. Reclaimed from
      // dateTable/description above rather than growing the grid's total
      // width.
      minWidth: 175,
      renderCell: ({ value } = {}) => <Icons.State state={value} />,
    },
    {
      field: 'taxReceipted',
      headerName: 'Tax Receipted',
      hide: true,
      showInExpandedRowPanel: false,
      flex: 1,
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'cancel',
      headerName: 'Cancel',
      hide: isNarrowerThanMd,
      flex: 0,
      minWidth: 40,
      sortable: false,
      disableExport: true,
      renderCell: ({
        row: {
          __typename,
          usedGiveGenius,
          amount,
          sentTo,
          recordId,
          state,
          initiatingFundId,
          charity: { accountName: name } = {},
          grantRequest: linkedGrantRequest,
          dafTransfer: linkedDafTransfer,
        } = {},
      }) => {
        if (__typename === 'GrantRequest' && (state === 'pending' || state === 'scheduled')) {
          return (
            <CancelButton
              id={recordId}
              title="Confirm"
              details={
                <Text.AltBody>
                  <b>{toAmountString(amount)}</b>
                  <br />
                  {name ? `: ${name}` : ''}
                  {usedGiveGenius && (
                    <Box sx={{ mt: 1 }}>This will also cancel the linked contribution funding this gift.</Box>
                  )}
                </Text.AltBody>
              }
              mutation={mutations.grantRequests.cancelPendingGrantRequest}
              refetchQueries={['MyFund', 'MyFundActivity']}
              variables={{ id: recordId, fundId }}
            />
          )
        }
        if (
          __typename === 'DafTransfer' &&
          (state === 'initiated' || state === 'issued') &&
          initiatingFundId === fundId
        ) {
          return (
            <CancelButton
              id={recordId}
              title="Confirm"
              details={
                <Text.AltBody>
                  <b>Amount:</b> {toAmountString(amount)}
                  <br />
                  <b>To:</b> {sentTo}
                  {usedGiveGenius && (
                    <Box sx={{ mt: 1 }}>This will also cancel the linked contribution funding this transfer.</Box>
                  )}
                </Text.AltBody>
              }
              mutation={mutations.dafTransfers.cancelDafTransfer}
              refetchQueries={['MyFund', 'MyFundActivity']}
              variables={{ id: recordId }}
            />
          )
        }
        if (
          __typename === 'Contribution' &&
          (state === 'scheduled' || state === 'initiated' || state === 'requested')
        ) {
          return (
            <CancelButton
              id={recordId}
              title="Confirm"
              details={
                <Text.AltBody>
                  <b>Contribution Amount:</b> {toAmountString(amount)}
                  {usedGiveGenius && linkedGrantRequest && (
                    <Box sx={{ mt: 1 }}>
                      This will also cancel the linked gift:
                      <br />
                      <b>
                        {toAmountString(linkedGrantRequest.amount)}
                        {linkedGrantRequest.charity?.accountName ? ` to ${linkedGrantRequest.charity.accountName}` : ''}
                      </b>
                    </Box>
                  )}
                  {usedGiveGenius && linkedDafTransfer && (
                    <Box sx={{ mt: 1 }}>
                      This will also cancel the linked transfer:
                      <br />
                      <b>
                        {toAmountString(linkedDafTransfer.amount)} to {linkedDafTransfer.sentTo}
                      </b>
                    </Box>
                  )}
                </Text.AltBody>
              }
              mutation={mutations.contributions.cancelContribution}
              refetchQueries={['MyFund', 'MyFundActivity', 'MyLinkedBanks', 'MyCreditCards', 'linkedZumRailsBanks']}
              variables={{ id: recordId, fundId }}
            />
          )
        }
        return null
      },
    },
  ]

  useEffect(() => {
    const applyExpandedHeight = (value, row) => ({ ...value, expanded: expandedHeight(row.id) })
    return gridApi?.current?.unstable_registerPipeProcessor('rowHeight', 'expanded-row-height', applyExpandedHeight)
  }, [expandedHeight])

  // Rendered via toolbarActions, not a separate row above the grid -- sits
  // as its own flex sibling immediately to the left of the export button,
  // same placement/styling as Request Liquidation's toolbarActions usage on
  // the Investment Account activity drill-down (investment-portfolio-activity.jsx).
  const toolbarActions =
    investmentPortfolios.length > 0 ? (
      <ActionButton
        actionKey="donor.investment_transfers.request_transfer"
        size="small"
        onClick={() => setRequestTransferOpen(true)}
        sx={{ fontSize: '16px', px: 3, py: 1 }}
      />
    ) : null

  return (
    <Container maxWidth="lg">
      <CardBox>
        <RequestInvestmentTransferDialog
          open={requestTransferOpen}
          onClose={() => setRequestTransferOpen(false)}
          fundId={fundId}
        />
        <DataGrid
          data-testid="giving-wallet-data-grid"
          ref={gridRef}
          apiRef={gridApi}
          disableColumnSelector
          rows={rows}
          columns={columns}
          loading={loading}
          toolbarActions={toolbarActions}
          filters={
            <Filters
              showGrantRequests={showGrantRequests}
              setShowGrantRequests={setShowGrantRequests}
              showContributions={showContributions}
              setShowContributions={setShowContributions}
              showBalanceAdjustements={showBalanceAdjustements}
              setShowBalanceAdjustements={setShowBalanceAdjustements}
              showDafTransfers={showDafTransfers}
              setShowDafTransfers={setShowDafTransfers}
              showInvestmentTransfers={showInvestmentTransfers}
              setShowInvestmentTransfers={setShowInvestmentTransfers}
            />
          }
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'desc' }],
            },
          }}
          components={{ Row: ExpandableRow }}
          componentsProps={{ row: { isExpanded } }}
          rowHeight={BASE_ROW_HEIGHT}
          getRowHeight={getRowHeight}
          sx={{
            '& .MuiDataGrid-cell': {
              p: '3px',
              // Top-align every cell at the same fixed offset instead of MUI's
              // default vertical centering -- see CELL_TOP_PADDING. Without
              // this, a wrapped Description cell centers its whole two-line
              // block, putting its first line above (and second line below)
              // where every other column's single centered line sits in that
              // same, now-taller row.
              alignItems: 'flex-start',
              pt: `${CELL_TOP_PADDING}px`,
            },
          }}
          localeText={{
            noRowsLabel: 'To make your first contribution, choose Add Funds.',
          }}
          exportFileName={`Giving Wallet Activity Table ${new Date().toISOString().split('T')[0]}`}
        />
      </CardBox>
    </Container>
  )
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text

  const truncated = text.slice(0, maxLength - 3)
  const lastSpaceIndex = truncated.lastIndexOf(' ')

  return `${lastSpaceIndex > 0 ? truncated.slice(0, lastSpaceIndex) : truncated}...`
}

function GrantRequestDescriptionRender({ descriptionValue, fundId, isGrantRequest }) {
  if (isGrantRequest) {
    const [charity] = descriptionValue
    return (
      <Text.AltBody>
        {charity && <Link to={`/funds/${fundId}/grant/${charity?.id}`}>{charity?.accountName}</Link>}
      </Text.AltBody>
    )
  }

  return descriptionValue
}
